import React, { Component } from 'react';
import classNames from 'classnames';
import Immutable from 'immutable';
import invariant from 'invariant';
import Debug from 'debug';
import {
	Editor,
	EditorState,
	ContentState,
	ContentBlock,
	RichUtils,
	convertToRaw,
	convertFromRaw,
	getDefaultKeyBinding,
	DefaultDraftInlineStyle,
	DefaultDraftBlockRenderMap,
	CompositeDecorator,
	Modifier,
} from 'draft-js';
import * as jsonpack from 'jsonpack';
import {
	unpackRawContent,
	MultiDecorator,
	ensureHasBlock,
	isEmptyContent,
} from 'lib/helpers';
import { Toolbar } from './toolbar';
import { ErrorBoundary } from 'lib/ErrorBoundary';

const debug = Debug('draft-master');

export class DraftEditor extends Component {

	/**
	 * 组件状态
	 */
	state = {
		/**
		 * 编辑器是否已经初始化
		 * 在 Server-rendering 的时候，编辑器不会初始化
		 */
		initialized: false,

		/**
		 * 编辑器状态
		 */
		editorState: EditorState.createEmpty(this._decorator),

		/**
		 * 编辑器目前是否具有焦点
		 */
		focused: false,

		/**
		 * 编辑器是否进入了只读模式
		 */
		readOnly: false,

		/**
		 * 编辑器提供给插件使用的内存
		 */
		pluginHostMemory: {},
	}

	/**
	 * 编辑器自己的 store
	 */
	_selfStore = {
		getEditorEnviorment: () => 'root',
		getEditorState: () => this.state.editorState,
		setEditorState: (editorState, forceChange) => this._handleChange(editorState, forceChange),
		getEditorReadOnly: () => this.state.readOnly,
		setEditorReadOnly: (readOnly) => this.setState({ readOnly }),
		getEditorRef: () => this.draftEditor,
	}

	/**
	 * 指定如何获取 activeStore 的方法，在 BlockHostAPI 会提供 API 进行设置
	 */
	_storeObtainer = null;

	/**
	 * 提供给插件调用的高级 API，可以替换 EditorStore
	 */
	_pluginHostAPI = {
		takeOverStore: (obtainer) => {
			this._storeObtainer = obtainer;
		},
		recoverStore: () => {
			this._storeObtainer = null;
		},
		readMemory: (select = x => x) => select(this.state.pluginHostMemory),
		updateMemory: (update = x => x) => new Promise(resolve => this.setState({
			pluginHostMemory: update(this.state.pluginHostMemory)
		}, resolve)),
		triggerCommand: (command, evt) => this._handleKeyCommand(command, this.state.editorState, evt),
		focusHostEditor: () => this.focus(),
		refreshToolbar: () => {
			if (this._toolbar) {
				this._toolbar.forceUpdate();
			}
		},
	}

	/**
	 * 当前活动的 store
	 * 可能是内联编辑器的 store，也可能是自己的 store
	 */
	get _activeStore() {
		if (this._storeObtainer) {
			const _activeStore = this._storeObtainer(this.state.pluginHostMemory);
			invariant(
				typeof _activeStore === 'object' &&
				typeof _activeStore.getEditorEnviorment === 'function' &&
				typeof _activeStore.getEditorState === 'function' &&
				typeof _activeStore.setEditorState === 'function',
				'The store obtainer that your plugin use to take over active store dones not return a valid editor store. ' +
				'It should contains getEditorEnviorment(), getEditorState() and setEditorState() methods. ' +
				'Now we got %s',
				{ toString: () => JSON.stringify(_activeStore) },
			);
			return _activeStore;
		}
		return this._selfStore;
	}

	/**
	 * 插件列表
	 * @type {DraftEditorPlugin[]}
	 */
	get _plugins() {
		return this.props.plugins;
	}

	/**
	 * 从插件获取应该使用的装饰器
	 * 此装饰器只会在编辑器初始化的时候使用，所以后续插件变更并不会变更装饰器的行为
	 */
	get _decorator() {
		const decorators = [];
		const draftDecorators = [];

		// 组装 decorator
		for (let { getDecorators, getDraftDecorators } of this._plugins) {
			if (getDecorators) {
				decorators.push(...getDecorators(this._selfStore));
			}
			if (getDraftDecorators) {
				draftDecorators.push(...getDraftDecorators(this._selfStore));
			}
		}
		if (draftDecorators.length) {
			decorators.push(new CompositeDecorator(draftDecorators));
		}
		if (decorators.length > 1) {
			return new MultiDecorator(decorators);
		}
		if (decorators.length === 1) {
			return decorators[0];
		}
		return null;
	}

	render() {
		const { disabled, containerClassName, className, placeholder, tip, containerStyle, style } = this.props;
		const { initialized, editorState, focused, readOnly } = this.state;

		if (!initialized) return null;

		const editorEnviorment = this._activeStore.getEditorEnviorment();
		const isRootEnviorment = editorEnviorment === 'root';

		return (
			<div
				className={classNames(
					'qa-r-editor-container',
					containerClassName,
					{ focused, disabled },
				)}
				style={containerStyle}>
				<Toolbar
					store={this._activeStore}
					plugins={this._plugins}
					ref={toolbar => this._toolbar = toolbar}
				/>
				<div
					className={classNames(
						"qa-r-editor draft-editor-host rno-markdown",
						className,
					)}
					style={style}
					onMouseDown={this._handleFocusByMouseDown}>
					<ErrorBoundary
						ref={errorBoundary => this.errorBoundary = errorBoundary}
						title="抱歉，编辑器发生错误"
						recover={
							<p>
								您可尝试
								<a href="javascript: void(0)" onClick={evt => {
									this.setRaw(this.getRaw());
									this.errorBoundary.clear();
								}}>
								恢复编辑内容
								</a>
							</p>
						}
						>
						<Editor
							ref={editor => this.draftEditor = editor}
							readOnly={!isRootEnviorment || readOnly} // 非主编辑器环境启用 readonly，防止不恰当的事件处理
							editorState={editorState}
							onChange={this._handleChange}
							onFocus={this._handleFocus}
							onBlur={this._handleBlur}
							customStyleMap={this._getCustomStyleMap()}
							blockStyleFn={this._getBlockStyle}
							blockRendererFn={this._renderBlock}
							blockRenderMap={DefaultDraftBlockRenderMap.merge(Immutable.Map({
								'code-block': {
									element: 'div'
								},
							}))}
							keyBindingFn={this._keyBindingFn}
							onUpArrow={this._handleArrowKey}
							onDownArrow={this._handleArrowKey}
							onLeftArrow={this._handleArrowKey}
							onRightArrow={this._handleArrowKey}
							handleReturn={this._handleReturnKey}
							handleKeyCommand={this._handleKeyCommand}
							onTab={this._handleTabKey}
							onSelect={e => e.preventDefault()}
							handlePastedText={this._handlePastedText}
							handlePastedFiles={this._handlePastedFiles}
							handleDroppedFiles={this._handleDroppedFiles}
						/>
					</ErrorBoundary>
					{ placeholder && isEmptyContent(editorState) &&
						<div className="rich-editor-placeholder">{ placeholder }</div>
					}
				</div>
				{tip && (
					<div className="qa-r-editor-tip">
						<div className="tip-cnt">{tip}</div>
					</div>
				)}
			</div>
		);
	}

	componentDidMount() {
		this._initContent();
		if (this.props.autoFocus) {
			this.focus();
		}
	}

	componentWillUnmount() {
		this.isUnmounted = true;
	}

	/**
	 * 获取编辑器的内容
	 * @public
	 */
	getContent() {
		return this.state.editorState.getCurrentContent();
	}

	/**
	 * 获取编辑器的纯文本内容
	 */
	getPlainText() {
		return this.getContent().getPlainText();
	}

	/**
	 * 获取编辑器的 Raw 格式数据
	 */
	getRaw() {
		return convertToRaw(this.getContent());
	}

	/**
	 * 获取经过 JSON Pack 压缩的打包内容
	 */
	getPacked() {
		return jsonpack.pack(this.getRaw());
	}

	/**
	 * @public
	 * 从外部更新编辑器的内容
	 * 默认编辑器不会从 props 同步 raw 内容，需要使用该方法进行更新
	 * @param {Draft.RawDraftContentState} raw
	 */
	setRaw(raw) {
		this._initContent({ raw });
	}

	/**
	 * @public
	 * 使编辑器获得编辑焦点
	 */
	focus = () => {
		if (this.draftEditor) {
			this.draftEditor.focus();
		}
	};

	/**
	 * @public
	 * 清空编辑内容
	 */
	clear = () => {
		this._storeObtainer = null;
		this.setState({
			editorState: EditorState.createEmpty(this._decorator),
			pluginHostMemory: {},
			readOnly: false,
		}, () => this.props.onChange(this));
		if (this.errorBoundary) {
			this.errorBoundary.clear();
		}
	};

	/**
	 * @private
	 * 从 props 初始化内容
	 */
	_initContent({ raw, packed } = this.props) {
		const importFromRaw = raw => this._setRaw(raw);
		const exportToRaw = () => convertToRaw(this.state.editorState.getCurrentContent());
		const checkRaw = raw => invariant(
			typeof raw === 'object' && Array.isArray(raw.blocks) && typeof raw.entityMap === 'object',
			'RichEditor 传入了非法的 raw 格式：%s',
			JSON.stringify(raw),
		);

		if (typeof window === 'object') {
			// for debug purpose
			window.re = { importFromRaw, exportToRaw, getStore: () => this._activeStore };
		}

		if (raw) {
			checkRaw(raw);
		}
		else if (!raw && packed) {
			raw = unpackRawContent(packed);
			invariant(
				raw,
				'RichEditor 无法解析 packed 数据：%s',
				packed,
			);
			checkRaw(raw);
		}

		if (raw) {
			importFromRaw(raw);
		}

		this.setState({ initialized: true });
	}

	/**
	 * @private
	 * @param {Draft.RawDraftContentState} raw
	 */
	_setRaw(raw) {
		this.setState({
			editorState: raw.blocks.length
				? EditorState.createWithContent(convertFromRaw(raw), this._decorator)
				: EditorState.createEmpty(this._decorator)
		});
	}

	_handleFocusByMouseDown = (/** @type {React.MouseEvent}*/ evt) => {
		this.focus();
	}

	/**
	 * @private
	 * 获取焦点
	 */
	_handleFocus = () => {
		this.setState({ focused: true });
		if (typeof this.props.onFocus === 'function') {
			this.props.onFocus();
		}
	}

	/**
	 * @private
	 * 失去焦点
	 */
	_handleBlur = () => {
		this.setState({ focused: false });
		if (typeof this.props.onBlur === 'function') {
			this.props.onBlur();
		}
	}

	_forceUpdate = () => {
		this.setState({
			editorState: EditorState.force(this.state.editorState, this.state.editorState.getSelection())
		});
	}

	/**
	 * @private
	 * 获取特殊 Entity 的样式
	 */
	_getCustomStyleMap = () => {
		let styleMap = {};
		for (let { getCustomStyleMap } of this._plugins) {
			if (getCustomStyleMap) {
				styleMap = { ...styleMap, ...getCustomStyleMap() };
			}
		}
		return styleMap;
	}

	/**
	 * @private
	 * 给指定的 block 添加 className
	 */
	_getBlockStyle = block => {
		if (block.getType() === 'unstyled') {
			return 'paragraph';
		}
	}

	/**
	 * @private
	 * 特定 block 的渲染方式
	 */
	_renderBlock = (/** @type {ContentBlock} */ block) => {
		for (let { getBlockRenderInfo } of this._plugins) {
			if (getBlockRenderInfo) {
				const info = getBlockRenderInfo(block, this._selfStore, this._pluginHostAPI);
				if (info) {
					return info
				}
			}
		}
		return null;
	}

	/**
	 * @private
	 * 处理新状态
	 */
	_handleChange = (/** @type {EditorState} */ editorState, forceChange) => {
		return new Promise(resolve => {
			// 检测内容是否发生变化
			const lastState = this.state.editorState;
			const lastContent = lastState.getCurrentContent();

			for (let { handleChange } of this._plugins) {
				if (handleChange) {
					editorState = handleChange(editorState);
				}
			}

			editorState = ensureHasBlock(editorState);

			const currentContent = editorState.getCurrentContent();
			const hasChanged = forceChange || currentContent !== lastContent;
			this.setState({ editorState }, () => {
				if (hasChanged && typeof this.props.onChange === 'function') {
					this.props.onChange(this);
				}
				resolve();
			});
		});
	};

	/**
	 * 尝试用插件指定的方法处理编辑事件，如果处理成功，将设置新的编辑器状态，并返回 "handled"，否则返回 "not-handled"
	 * @param {keyof DraftEditorPlugin} pluginMethodName 插件方法名
	 * @param {any[]} pluginMethodArgs 插件方法参数
	 */
	_tryHandleByPlugins(pluginMethodName, ...pluginMethodArgs) {
		for (let plugin of this._plugins) {
			if (plugin[pluginMethodName]) {
				try {
					const result = plugin[pluginMethodName](...pluginMethodArgs);
					if (result === 'handled') {
						debug(`${pluginMethodName} handled by ${plugin.name}`);
						return 'handled';
					}
				}
				catch (err) {
					console.error(`使用编辑器插件 ${plugin.name} 进行 ${pluginMethodName}() 时出错：${err.message}`);
					throw err;
				}
			}
		}
		return 'not-handled';
	}

	/**
	 * @private
	 * 从键盘事件解析出编辑器命令
	 */
	_keyBindingFn = (/** @type {React.KeyboardEvent}*/ evt) => {
		const { editorState } = this.state;
		for (let { keyBinding } of this._plugins) {
			if (keyBinding) {
				const command = keyBinding(editorState, evt);
				if (command) {
					return command;
				}
			}
		}
		return getDefaultKeyBinding(evt);
	};

	/**
	 * @private
	 * 处理编辑器命令
	 */
	_handleKeyCommand = (
		/** @type {string} */ command,
		/** @type {EditorState} */ editorState,
		/** @type {React.KeyboardEvent} */ evt,
	) => {
		const store = this._activeStore;
		return this._tryHandleByPlugins('handleCommand', store, command, evt);
	};

	/**
	 * @private
	 * 处理方向按键
	 */
	_handleArrowKey = (/** @type {React.KeyboardEvent} */evt) => {
		const editorState = this.state.editorState;
		const command = {
			37: 'arrow-left',
			38: 'arrow-up',
			39: 'arrow-right',
			40: 'arrow-down',
		}[evt.keyCode];
		return this._handleKeyCommand(command, editorState, evt);
	}

	/**
	 * @private
	 * 处理回车键
	 */
	_handleReturnKey = (/** @type {React.KeyboardEvent} */evt) => {
		const { editorState } = this.state;
		return this._handleKeyCommand('return', editorState, evt);
	}

	/**
	 * @private
	 * 处理 Tab 键
	 */
	_handleTabKey = (/** @type {React.KeyboardEvent} */evt) => {
		const { editorState } = this.state;
		return this._handleKeyCommand('tab', editorState, evt);
	}

	/**
	 * @private
	 * 处理文本粘贴
	 */
	_handlePastedText = (text, html) => {
		if (!text) {
			return 'not-handled';
		}

		const store = this._activeStore;
		const editorState = store.getEditorState();
		let result = null;

		// 插件尝试处理是否能粘贴 HTML 内容
		if (html) {
			result = this._tryHandleByPlugins('handlePastedHtml', store, html);
			if (result === 'handled') {
				return result;
			}
		}

		// 插件尝试处理是否能粘贴纯文本内容
		result = this._tryHandleByPlugins('handlePastedText', store, text);

		if (result !== 'handled') {
			// 默认当做纯文本粘贴，按照换行符切块

			// 浏览器不能正常渲染 Tab
			text = text.replace(/\t/g, '    ');
			// 统一换行符
			text = text.replace(/r\n/g, '\n');
			const selection = editorState.getSelection();
			const blockMap = ContentState.createFromText(text, '\n').getBlockMap();
			const newContent = Modifier.replaceWithFragment(editorState.getCurrentContent(), selection, blockMap);
			this._handleChange(EditorState.push(editorState, newContent, 'insert-fragment'));

			result = 'handled';
		}
		return result;
	};

	/**
	 * @private
	 * 处理文件拖放
	 */
	_handleDroppedFiles = (/** @type {Draft.SelectionState}*/ selection, /** @type {Array<Blob>} */ files) => {
		const store = this._activeStore;
		return this._tryHandleByPlugins('handleFiles', store, selection, files, 'drop');
	};

	/**
	 * @private
	 * 处理文件粘贴
	 */
	_handlePastedFiles = (files) => {
		const store = this._activeStore;
		const selection = store.getEditorState().getSelection();
		return this._tryHandleByPlugins('handleFiles', store, selection, files, 'paste');
	}
}



// WEBPACK FOOTER //
// ./lib/editor/DraftEditor/DraftEditor.jsx